import React, {useEffect, useRef, useState} from 'react';

import AriaLiveContainer from '@/components/AriaLiveMessage/AriaLiveContainer';
import {Popover, PopoverAnchor} from '@/components/Popover/Popover';
import pushContentTypes from '@/consts/inbox/pushContentTypes';
import * as localizationKeys from '@/consts/localization/localizationKeys';
import useNotificationDetails from '@/hooks/inbox/useNotificationDetails';
import amplitudeInboxTracking from '@/services/analytics/amplitude/amplitudeInboxTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import domService from '@/services/domService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import {twx} from '@/utils/tailwindUtils';

import InboxPopoverContent from './components/InboxPopoverContent';
import InboxPopoverList from './components/InboxPopoverList';
import InboxPopoverNotificationDetails from './components/InboxPopoverNotificationDetails';
import useOpenInboxDetails from './useOpenInboxDetails';

const InboxPopover = ({isOpen, setIsOpen, children}) => {
    const ref = useRef(null);
    const backButtonRef = useRef(null);
    const [messageId, setMessageId] = useState(null);
    const [isMessageOpened, setIsMessageOpened] = useState(false);
    const [lastFocusedMessageId, setLastFocusedMessageId] = useState(null);
    const localizedStrings = getLocalizedStrings();
    const {message, isLoaderActive} = useNotificationDetails({
        messageId,
    });
    const isLoyaltyPushContentType = pushContentTypes.isPushContentTypeLoyalty(message?.pushContentType);
    const isDark = isLoyaltyPushContentType;

    useEffect(() => {
        if (!isOpen) return;

        amplitudeInboxTracking.trackInboxScreen();
        googleAnalyticsService.trackInboxScreenView();
    }, [isOpen]);

    const onMessageClick = (messageId) => {
        setLastFocusedMessageId(messageId);
        setMessageId(messageId);
        setIsMessageOpened(true);
    };

    useOpenInboxDetails({setMessageId: onMessageClick, setIsOpen});

    const onOpenChange = (isOpen) => {
        setIsOpen(isOpen);

        if (!isOpen) {
            closeNotificationDetails();
            setMessageId();
        }
    };
    const closeNotificationDetails = () => {
        setIsMessageOpened();
    };

    const handleAnimationEnd = () => {
        if (isMessageOpened && backButtonRef.current) {
            backButtonRef.current.focus();
        } else if (lastFocusedMessageId) {
            setMessageId();
            const notificationEl = domService.getElementById(lastFocusedMessageId);

            if (notificationEl) {
                notificationEl.focus();
            }
        }
    };

    return (
        <Popover open={isOpen} onOpenChange={onOpenChange}>
            <PopoverAnchor>{children}</PopoverAnchor>
            <InboxPopoverContent isDark={isDark} ref={ref}>
                {isOpen && (
                    <div
                        className={twx(
                            'transition-height relative h-[480px] overflow-hidden rounded-xl duration-300 ease-in-out',
                            isMessageOpened && 'h-[556px]'
                        )}
                    >
                        <AriaLiveContainer>
                            {
                                localizedStrings[
                                    isMessageOpened
                                        ? localizationKeys.INBOX_DETAILS_VIEW_ARIA_LIVE_MESSAGE
                                        : localizationKeys.INBOX_LIST_VIEW_ARIA_LIVE_MESSAGE
                                ]
                            }
                        </AriaLiveContainer>
                        <AnimatedContainer
                            className={isMessageOpened ? '-translate-x-full' : 'translate-x-0'}
                            inert={isMessageOpened}
                            onTransitionEnd={handleAnimationEnd}
                        >
                            <InboxPopoverList onMessageClick={onMessageClick} containerRef={ref} />
                        </AnimatedContainer>
                        <AnimatedContainer
                            className={twx(
                                isMessageOpened ? 'translate-x-0' : 'translate-x-full',
                                isDark && 'bg-primary-dark text-romance'
                            )}
                            inert={!isMessageOpened}
                        >
                            {(message || isMessageOpened) && (
                                <InboxPopoverNotificationDetails
                                    message={message}
                                    isLoaderActive={isLoaderActive}
                                    closeNotificationDetails={closeNotificationDetails}
                                    containerRef={ref}
                                    backButtonRef={backButtonRef}
                                    isDark={isDark}
                                />
                            )}
                        </AnimatedContainer>
                    </div>
                )}
            </InboxPopoverContent>
        </Popover>
    );
};

export default InboxPopover;

const AnimatedContainer = ({className, children, inert, ...rest}) => {
    return (
        <div
            className={twx(
                'absolute inset-0 flex flex-col overflow-auto transition-transform duration-300 ease-in-out',
                className
            )}
            inert={domService.getInertFromBool(inert)}
            {...rest}
        >
            {children}
        </div>
    );
};
